import React, { useState } from 'react';
import ReCAPTCHA from "react-google-recaptcha";
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { mockData } from '../data/mock';


const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    budget: '',
    message: '',
    timeline: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCaptchaChange = (value) => {
    if (value) setCaptchaVerified(true);
  };

  const handleSubmit = async (e) => {
  e.preventDefault();

  if (!formData.name || !formData.email || !formData.message) {
    toast({ title: "Missing Information", description: "Name, email, and message are required." });
    return;
  }

  setIsSubmitting(true);

  try {
    const response = await fetch("/send.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData)
    });

    const json = await response.json();

    if (json.success) {
      toast({ title: "Message Sent!", description: "We will contact you shortly." });

      setFormData({
        name: "",
        email: "",
        company: "",
        phone: "",
        service: "",
        budget: "",
        message: "",
        timeline: ""
      });

    } else {
      toast({ title: "Error", description: json.error || "Failed to send message." });
    }
  } catch (err) {
    toast({ title: "Network Error", description: err.message });
  }

  setIsSubmitting(false);
};


  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary to-primary-dark pt-28" style={{ paddingTop: '6.5rem' }}>
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="hero-text mb-6">Get In Touch</h1>
            <p className="text-xl text-gray-800 leading-relaxed">
              Ready to start your project? We'd love to hear about your vision and discuss how to bring it to life.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section-padding bg-white">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="section-header mb-6">Let's Connect</h2>
                <p className="text-gray-700 leading-relaxed mb-8">
                  Whether you have a project in mind or just want to explore possibilities, we're here to help.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center">
                    <Mail size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-500 mb-1">Email Us</h3>
                    <p className="text-gray-600"><a href="mailto:contact@ns7visuals.com" className="hover:text-blue-400 transition-colors">
                  contact@ns7visuals.com
                </a></p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center">
                    <Phone size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-500 mb-1">Call Us</h3>
                    <p className="text-gray-600"><a href="https://wa.me/918698603964" target="_blank" rel="noopener noreferrer" className="hover:text-green-400 transition-colors">
                  +91 8698603964
                </a></p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center">
                    <MapPin size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-500 mb-1">Visit Us</h3>
                    <p className="text-gray-600"><a href="https://www.google.com/maps?q=Mumbai,+Maharashtra" target="_blank"rel="noopener noreferrer"className="hover:text-yellow-400 transition-colors">
                  Mumbai, Maharashtra
                </a></p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center">
                    <Clock size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-500 mb-1">Business Hours</h3>
                    <p className="text-gray-600">{mockData.company.contact.hours}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="card-white">
                <h3 className="subsection-header mb-6">Send us a message</h3>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  
                  {/* Basic Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Full Name *</label>
                      <Input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="Alex"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Email Address *</label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                  </div>

                  {/* Company & Phone */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Company</label>
                      <Input
                        type="text"
                        value={formData.company}
                        onChange={(e) => handleInputChange('company', e.target.value)}
                        placeholder="Your Company"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Phone Number</label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        placeholder="+91 0000000000"
                      />
                    </div>
                  </div>

                  {/* Service & Budget */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Service Needed *</label>
                      <Select onValueChange={(value) => handleInputChange('service', value)}>
                        <SelectTrigger className="bg-white border border-gray-300 text-gray-800 hover:border-gray-500 transition">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent className="bg-gradient-to-br from-white to-gray-100 text-gray-800 shadow-lg rounded-lg border border-gray-200">
                          <SelectItem value="brand-identity" className="hover:bg-gray-200 cursor-pointer">Brand Identity</SelectItem>
                          <SelectItem value="web-development" className="hover:bg-gray-200 cursor-pointer">Web Development</SelectItem>
                          <SelectItem value="ui-ux" className="hover:bg-gray-200 cursor-pointer">UI/UX Design</SelectItem>
                          <SelectItem value="not-sure" className="hover:bg-gray-200 cursor-pointer">Not Sure</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Project Budget</label>
                      <Select onValueChange={(value) => handleInputChange('budget', value)}>
                        <SelectTrigger className="bg-white border border-gray-300 text-gray-800 hover:border-gray-500 transition">
                          <SelectValue placeholder="Select budget range" />
                        </SelectTrigger>
                        <SelectContent className="bg-gradient-to-br from-white to-gray-100 text-gray-800 shadow-lg rounded-lg border border-gray-200">
                          <SelectItem value="under-5k" className="hover:bg-gray-200 cursor-pointer">Under $5,000</SelectItem>
                          <SelectItem value="5k-10k" className="hover:bg-gray-200 cursor-pointer">$5,000 - $10,000</SelectItem>
                          <SelectItem value="10k-25k" className="hover:bg-gray-200 cursor-pointer">$10,000 - $25,000</SelectItem>
                          <SelectItem value="25k-50k" className="hover:bg-gray-200 cursor-pointer">$25,000 - $50,000</SelectItem>
                          <SelectItem value="over-50k" className="hover:bg-gray-200 cursor-pointer">Over $50,000</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Timeline */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Project Timeline</label>
                    <Select onValueChange={(value) => handleInputChange('timeline', value)}>
                      <SelectTrigger className="bg-white border border-gray-300 text-gray-800 hover:border-gray-500 transition">
                        <SelectValue placeholder="When do you need this completed?" />
                      </SelectTrigger>
                      <SelectContent className="bg-gradient-to-br from-white to-gray-100 text-gray-800 shadow-lg rounded-lg border border-gray-200">
                        <SelectItem value="asap" className="hover:bg-gray-200 cursor-pointer">ASAP</SelectItem>
                        <SelectItem value="1-month" className="hover:bg-gray-200 cursor-pointer">Within 1 month</SelectItem>
                        <SelectItem value="2-3-months" className="hover:bg-gray-200 cursor-pointer">2-3 months</SelectItem>
                        <SelectItem value="3-6-months" className="hover:bg-gray-200 cursor-pointer">3-6 months</SelectItem>
                        <SelectItem value="6-months-plus" className="hover:bg-gray-200 cursor-pointer">6+ months</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>


                  {/* Message */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Project Details *</label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      placeholder="Tell us about your project, goals, and any specific requirements..."
                      rows={6}
                      required
                      className="resize-none"
                    />
                  </div>

                  {/* ✅ reCAPTCHA Integration */}
                  <div className="flex justify-center">
                    <ReCAPTCHA
                      sitekey="6Ldtff8rAAAAAID8Hmu-tj90xIvu2cfNxyle9rsh"
                      onChange={handleCaptchaChange}
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-black text-white hover:bg-gray-800 py-3 text-lg"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message <Send size={18} className="ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
